import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Receipt, Wallet } from 'lucide-react';
import { StatusBadge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { ROUTES } from '@/app/router/routes';
import { Stat } from '@/features/library/shared';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate, formatMoney, humanise } from '@/utils/format';
import { PortalShell, type PortalContext } from './PortalShell';
import { SectionTitle } from './shared';

/**
 * What the family owes and has paid.
 *
 * Every figure — the balance, each invoice's outstanding amount, each payment —
 * is the server's. A receipt opens on its own page so it prints exactly like the
 * office's copy.
 */
export function PortalFeesPage() {
  return (
    <PortalShell title="Fees" icon={<Wallet className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <Fees context={context} />}
    </PortalShell>
  );
}

function Fees({ context }: { context: PortalContext }) {
  const { subject, subjectKey, withChild } = context;

  const fees = useQuery({
    queryKey: queryKeys.portal.fees(subjectKey),
    queryFn: () => portalApi.fees(subject),
  });

  if (fees.isPending) {
    return (
      <Card>
        <LoadingState label="Loading fees" />
      </Card>
    );
  }
  if (fees.isError) {
    return (
      <Card>
        <ErrorState error={fees.error} onRetry={() => void fees.refetch()} />
      </Card>
    );
  }

  const { balance, invoices, payments } = fees.data;
  const currency = balance.currencyCode;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Due now"
          value={<span className="break-words">{formatMoney(balance.netDue, currency)}</span>}
          tone={balance.netDue > 0 ? 'warning' : 'success'}
          hint={
            balance.creditHeld > 0
              ? `After ${formatMoney(balance.creditHeld, currency)} held on account`
              : `${balance.unpaidInvoiceCount} unpaid invoice${balance.unpaidInvoiceCount === 1 ? '' : 's'}`
          }
        />
        <Stat
          label="Overdue"
          value={<span className="break-words">{formatMoney(balance.overdueAmount, currency)}</span>}
          tone={balance.overdueAmount > 0 ? 'danger' : 'neutral'}
        />
        <Stat
          label="Billed"
          value={<span className="break-words">{formatMoney(balance.totalBilled, currency)}</span>}
          hint={`${balance.invoiceCount} invoice${balance.invoiceCount === 1 ? '' : 's'}`}
        />
        <Stat
          label="Paid"
          value={<span className="break-words">{formatMoney(balance.totalPaid, currency)}</span>}
          tone="success"
          hint={
            balance.totalDiscounts > 0 || balance.totalScholarships > 0
              ? `Discounts ${formatMoney(balance.totalDiscounts, currency)} · scholarships ${formatMoney(balance.totalScholarships, currency)}`
              : undefined
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionTitle>Invoices</SectionTitle>
          {invoices.length === 0 ? (
            <EmptyState title="No invoices" description="Nothing has been billed yet." />
          ) : (
            <ul className="divide-y divide-line">
              {invoices.map((invoice) => (
                <li key={invoice.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-xs font-semibold text-ink">
                      {invoice.invoiceNumber}
                    </span>
                    <span
                      className={
                        invoice.daysOverdue > 0
                          ? 'block text-[11px] font-semibold text-danger'
                          : 'block text-[11px] text-subtle'
                      }
                    >
                      {[invoice.billingPeriod ?? invoice.termName, `due ${formatDate(invoice.dueDate)}`]
                        .filter(Boolean)
                        .join(' · ')}
                      {invoice.daysOverdue > 0 &&
                        ` · ${invoice.daysOverdue} day${invoice.daysOverdue === 1 ? '' : 's'} overdue`}
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="block text-sm font-semibold tabular-nums text-ink">
                      {formatMoney(invoice.outstandingAmount, invoice.currencyCode)}
                    </span>
                    <span className="block text-[11px] tabular-nums text-subtle">
                      of {formatMoney(invoice.totalAmount, invoice.currencyCode)}
                    </span>
                  </span>
                  <StatusBadge status={invoice.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionTitle>Payments</SectionTitle>
          {payments.length === 0 ? (
            <EmptyState title="No payments" description="Payments appear here once the school records them." />
          ) : (
            <ul className="divide-y divide-line">
              {payments.map((payment) => (
                <li key={payment.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold tabular-nums text-ink">
                      {formatMoney(payment.amount, payment.currencyCode)}
                    </span>
                    <span className="block text-[11px] text-subtle">
                      {formatDate(payment.paymentDate)} · {humanise(payment.paymentMethod)} ·{' '}
                      <span className="font-mono">{payment.receiptNumber}</span>
                    </span>
                  </span>
                  <StatusBadge status={payment.status} />
                  <Link
                    to={withChild(ROUTES.myReceipt(payment.id))}
                    className="inline-flex items-center gap-1.5 border border-line px-2.5 py-1 text-xs font-semibold text-ink transition-colors hover:bg-surface-2"
                  >
                    <Receipt className="h-3.5 w-3.5" aria-hidden="true" />
                    Receipt
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
